/* A11y enhancements — Eastland Shoe
 *
 * 1) Keyboard access for the qikify Smart Menu desktop mega-menu.
 *    The app renders submenu panels only on mouse hover (pointer events);
 *    triggers advertise aria-haspopup/aria-expanded but implement no keyboard
 *    support. This shim translates keyboard interaction into the hover events
 *    the app already handles: ArrowDown/Space on a top-level item opens its
 *    panel and moves focus into it, Escape closes and restores focus, and the
 *    panel closes automatically when focus leaves it.
 *
 * 2) "Opens in a new window" cues on target="_blank" links injected by apps
 *    (Shopify consent banner policy link, app watermarks, popups) that the
 *    theme's Liquid cannot edit. Theme-owned links carry their cues in Liquid;
 *    this sweep only patches links that have no cue yet.
 */
(function () {
  'use strict';

  /* ---------- 1. Mega-menu keyboard support ---------- */

  var ITEM_SEL = '.header__inline-menu li.tmenu_item_has_child';
  var TRIGGER_SEL = ITEM_SEL + ' > a';
  var PANEL_SEL = '.tmenu_submenu';
  var keyboardOpened = new Set();

  function fire(el, type, opts) {
    var Ev = type.indexOf('pointer') === 0 && window.PointerEvent ? PointerEvent : MouseEvent;
    el.dispatchEvent(new Ev(type, opts));
  }

  function openItem(li, trigger) {
    var r = trigger.getBoundingClientRect();
    var at = { bubbles: true, cancelable: true, clientX: r.left + r.width / 2, clientY: r.top + r.height / 2 };
    var enter = { bubbles: false, cancelable: true, clientX: at.clientX, clientY: at.clientY };
    fire(li, 'pointerover', at);
    fire(li, 'pointerenter', enter);
    fire(li, 'mouseover', at);
    fire(li, 'mouseenter', enter);
    fire(trigger, 'pointerover', at);
    fire(trigger, 'mouseover', at);
  }

  function closeItem(li) {
    var at = { bubbles: true, cancelable: true };
    var leave = { bubbles: false, cancelable: true };
    fire(li, 'pointerout', at);
    fire(li, 'pointerleave', leave);
    fire(li, 'mouseout', at);
    fire(li, 'mouseleave', leave);
    keyboardOpened.delete(li);
  }

  function focusFirstPanelLink(li, triesLeft) {
    var panel = li.querySelector(PANEL_SEL);
    var link = panel && panel.querySelector('a[href]');
    if (link && getComputedStyle(panel).visibility === 'visible') {
      link.focus();
      if (document.activeElement === link) {
        guardFocus(li, link, 6);
        return;
      }
    }
    if (triesLeft > 0) {
      setTimeout(function () { focusFirstPanelLink(li, triesLeft - 1); }, 100);
    }
  }

  /* The app can snap focus back to the trigger while its open animation
     settles; briefly re-assert the panel link, never fighting real tabbing
     (only acts when focus sits on the trigger of a keyboard-opened item). */
  function guardFocus(li, link, checks) {
    if (checks <= 0) return;
    setTimeout(function () {
      var trigger = li.querySelector(':scope > a');
      if (keyboardOpened.has(li) && document.activeElement === trigger && li.contains(link)) {
        link.focus();
      }
      guardFocus(li, link, checks - 1);
    }, 150);
  }

  document.addEventListener('keydown', function (e) {
    var trigger = e.target.closest && e.target.closest(TRIGGER_SEL);
    if (trigger) {
      var li = trigger.closest(ITEM_SEL);
      if (e.key === 'ArrowDown' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        keyboardOpened.add(li);
        openItem(li, trigger);
        focusFirstPanelLink(li, 20);
      } else if (e.key === 'Escape' || e.key === 'Esc') {
        closeItem(li);
      }
      return;
    }
    if ((e.key === 'Escape' || e.key === 'Esc') && e.target.closest && e.target.closest(PANEL_SEL)) {
      var openLi = e.target.closest(ITEM_SEL);
      if (openLi) {
        var openTrigger = openLi.querySelector(':scope > a');
        closeItem(openLi);
        if (openTrigger) openTrigger.focus();
        e.preventDefault();
      }
    }
  });

  /* Close a keyboard-opened panel once focus moves outside its item. */
  document.addEventListener('focusin', function () {
    keyboardOpened.forEach(function (li) {
      if (!li.contains(document.activeElement)) closeItem(li);
    });
  });

  /* ---------- 2. New-window cues on app-injected links ---------- */

  var CUE = ' (opens in a new window)';

  function hasCue(link) {
    var text = ((link.getAttribute('aria-label') || '') + ' ' + link.textContent + ' ' + (link.title || '')).toLowerCase();
    return text.indexOf('new window') !== -1 || text.indexOf('new tab') !== -1 || text.indexOf('opens in') !== -1;
  }

  function addCue(link) {
    if (link.dataset.a11yNewWindow) return;
    link.dataset.a11yNewWindow = '1';
    if (hasCue(link)) return;
    var label = link.getAttribute('aria-label');
    if (label) {
      link.setAttribute('aria-label', label + CUE);
    } else {
      var span = document.createElement('span');
      span.className = 'visually-hidden';
      span.textContent = CUE;
      link.appendChild(span);
    }
  }

  /* ---------- 3. Alt text for app-rendered photos ---------- */

  /* The Shopify Collabs page block has no alt field for its photos. Keyed by
     upload id: if the photos are changed in the app, add the new ids here
     (unknown photos keep their empty alt and are skipped by screen readers). */
  var APP_IMAGE_ALT = {
    '7c55f54aac0cdac7d677fa6b27d217fc': 'Woman in a navy top and striped pants wearing mint green penny loafers outside a shop door',
    '4a7754ea43c394135404be97193f7802': 'Brown camp mocs worn with cuffed jeans on a gravel path',
    '8429885d934a1eeebb5a539c9fa2d851': 'Woman in a yellow dress wearing burgundy penny loafers at a white cafe table',
    'ef664773468e88127f664dfafd3cd30d': 'Brown moc toe boots worn with cuffed jeans on a wet wooden dock'
  };

  function addAppAlt(img) {
    if (img.getAttribute('alt')) return;
    var match = /brand-profile-media\/([0-9a-f]+)\./.exec(img.getAttribute('src') || '');
    if (match && APP_IMAGE_ALT[match[1]]) img.setAttribute('alt', APP_IMAGE_ALT[match[1]]);
  }

  /* ---------- 4. Empty layout-only menu links ---------- */

  /* The menu app renders an untitled column (used only to lay out images) as a
     link with no text. Take it out of the tab order and the accessibility tree. */
  function hideEmptyMenuLink(link) {
    if (link.textContent.trim() || link.querySelector('img, svg')) return;
    link.setAttribute('aria-hidden', 'true');
    link.setAttribute('tabindex', '-1');
  }

  function sweep() {
    document.querySelectorAll('a[target="_blank"]').forEach(addCue);
    document.querySelectorAll('img.collabs-page__image').forEach(addAppAlt);
    document.querySelectorAll('a.tmenu_item_link').forEach(hideEmptyMenuLink);
  }

  sweep();

  var sweepTimer = null;
  new MutationObserver(function () {
    clearTimeout(sweepTimer);
    sweepTimer = setTimeout(sweep, 300);
  }).observe(document.body, { childList: true, subtree: true });
})();
